import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FromBuilderItem } from './form-item';
import { TitleElement } from './elements/title-element';
import dayjs from 'dayjs';
import { cloneDeep, get, isEqual, set } from 'lodash';
import { DATE_FORMAT } from '../../constants';
import { useUserFormRole } from '../../hook/useUserFormRole';
import { ADD_TO_COPY_GROUP_SETTING_KEY } from '../from-builder/elements-settings/components/add-to-copy-group-setting';
import { GridYValue, GridYUnit } from '../../constants';
import { isMobile, isMobileOnly } from 'react-device-detect';
import { AUTO_ADD_INFO_FOR } from '../from-builder/elements-settings/submitters-field-element-settings';
import { useIsInitFormPage } from '../../hook/pages/useIsInitFormPage';
import { replaceValuesInText } from '../../utility';
import { multilineGrowthPushes } from '../../utility/printMultilineGrowth';
import { hidesContent, textContentBottom } from './elements/text-element';

// The whole fields area is pulled up by this many (scaled) pixels so it sits
// right under the title instead of under the empty header space kept by the
// builder canvas.
const FIELDS_TOP_SHIFT = 100;
const FIELDS_TOP_SHIFT_MOBILE = 70;
// Every field is drawn 2 grid units above its stored `top` (see `positionData`
// in form-item.js), so the lowest field ends that much higher than the raw
// area height.
const FIELD_TOP_GRID_OFFSET = 2;
// Breathing room under the last field, in px. Kept a little generous: fields
// that grow while the form is filled in (a `text` element's `topShift`, for
// one) end lower than their stored position predicts, and running out of room
// gives `.render-form` a scrollbar.
const FORM_BOTTOM_PADDING = 60;
// Room kept under the copy group for the "Copy a group of fields" button.
const COPY_GROUP_BUTTON_RESERVE = 5;

export const RenderForm = function RenderForm({
  formData,
  formElement: formElements,
  title,
  handelInitSettings,
  isPrint,
  isSubmitter,
  isDisabled,
  errors,
  duplicateElement,
  removeElement,
  highlightPrevious,
  formHasOnlyOneSubmitter,
  initiatorID,
  onSend,
  autoFillingVariables,
  submitterType,
  submitterInfo,
  previewTabItem,
  isPreview,
  showDummyActions,
  strictOrderStep,
  submitter2Info,
}) {
  const userFormRole = useUserFormRole();
  const renderFormRef = useRef();
  const isInitFormPage = useIsInitFormPage();

  // On phones (and the "Phone" preview tab) fields are laid out one under
  // another instead of being absolutely positioned, ordered by their real
  // position on the canvas. Print output always keeps the designed layout.
  const isMobileView = !isPrint && (isMobileOnly || previewTabItem === 'phone');

  const [lastIndex, setLastIndex] = useState(null);
  const [scale, setScale] = useState(null);
  const [formFieldsAdditionalHeight, setFormFieldsAdditionalHeight] = useState({});
  const [submitterAddInfoElementID, setSubmitterAddInfoElementID] = useState(null);
  const [isFormPrepared, setIsFormPrepared] = useState(false);
  // Print only: a multiline answer prints in full and can outgrow its box, and so does a
  // text that would scroll on screen (`expanded`), so the fields they would run over move
  // down (utility/printMultilineGrowth). `moves` and `growth` are px by field id.
  const [printGrowth, setPrintGrowth] = useState({ moves: {}, growth: {}, expanded: {} });
  const printGrowthRef = useRef(printGrowth);
  printGrowthRef.current = printGrowth;
  const fieldsAreaRef = useRef();

  const isExistElementsCopyGroup = useMemo(() => {
    let value = false;
    if (Array.isArray(formElements)) {
      value = formElements.some((item) => !!get(item, `options[${ADD_TO_COPY_GROUP_SETTING_KEY}]`));
    }
    return value;
  }, [formElements]);

  const lastCopyGroupElementIndex = useMemo(() => {
    if (Array.isArray(formElements)) {
      return formElements.findLastIndex(
        (item) => !!get(item, `options[${ADD_TO_COPY_GROUP_SETTING_KEY}]`) || !!get(item, `options[copyGroupID]`),
      );
    }
    return -1;
  }, [formElements]);

  // Bottom edge of the last copy-group field in *stored* coordinates: the point a
  // new copy is inserted at, and the line that decides which fields move down for
  // the "Copy a group of fields" button. Where that button is actually drawn is a
  // separate, rendered coordinate — see `copyGroupButtonTop` in `renderFieldsData`.
  const copyGroupInsertTop = useMemo(() => {
    const field = formElements?.[lastCopyGroupElementIndex];
    if (!field) return null;
    return (field.options.top ?? 0) + (field.options.height ?? 0);
  }, [formElements, lastCopyGroupElementIndex]);

  const showsCopyGroupButton = !isPrint && isSubmitter && isExistElementsCopyGroup && copyGroupInsertTop !== null;

  // Fields below the button move down to leave room for it. The test is on the
  // field's own `top`, never on its place in `form_fields`: that array is in
  // authoring order, so a field drawn higher up the canvas can still come after
  // the copy group in it, and shifting that one down drops it onto the fields
  // above the group.
  const copyGroupButtonReserveFor = (field) => {
    if (!isSubmitter || !isExistElementsCopyGroup || copyGroupInsertTop === null) return 0;
    return (field.options.top ?? 0) >= copyGroupInsertTop ? COPY_GROUP_BUTTON_RESERVE : 0;
  };

  // Bottom position of the copy-group template fields — used as the insert
  // point when copying a group in the mobile (vertical) layout.
  const copyGroupTemplateBottom = useMemo(() => {
    if (!Array.isArray(formElements)) return 0;
    const templateFields = formElements.filter((item) => !!get(item, `options[${ADD_TO_COPY_GROUP_SETTING_KEY}]`));
    if (!templateFields.length) return 0;
    return Math.max(...templateFields.map((item) => (item.options.top ?? 0) + (item.options.height ?? 0)));
  }, [formElements]);

  const errorsObj = useMemo(() => {
    return errors?.reduce((acc, item) => {
      const [id, errorMessage] = Object.entries(item)?.flat() ?? [];
      acc[Number(id)] = errorMessage;
      return acc;
    }, {});
  }, [errors]);

  useEffect(() => {
    if (formElements) {
      setLastIndex(formElements.findLastIndex((item) => item.options.canAddNew));
    }
  }, [formElements]);

  // Every "Submitter" field belongs to whoever is filling the form in, so they all
  // get auto-filled -- but one per render, not all at once: `handelInitSettings`
  // replaces the whole `form_fields` array built from a closure over the current
  // one, so two fields writing in the same commit would clobber each other.
  // Pointing at the first *empty* field and recomputing once it has been filled
  // walks the list one field per render, skipping any field a previous submitter
  // already filled in, and settles on `undefined` when none are left.
  useEffect(() => {
    if (formElements) {
      setSubmitterAddInfoElementID(
        formElements.find(
          (item) =>
            item.type === 'submitters-field' &&
            item.options.autoAddInfoFor?.value === AUTO_ADD_INFO_FOR.SUBMITTER &&
            !item.options.valueInput,
        )?.id,
      );
    }
  }, [formElements]);

  useEffect(() => {
    if (formElements && !isFormPrepared) {
      if (!isInitFormPage) {
        const formFields = cloneDeep(formElements);
        formFields.forEach((item) => {
          if (item.type === 'text') {
            item.options.valueInput = replaceValuesInText(
              item.options.valueInput || item.options.value,
              autoFillingVariables,
              submitterType,
              submitterInfo,
            );
          } else if (item.type === 'single-line') {
            item.options.valueInput = replaceValuesInText(item.options.valueInput, autoFillingVariables, submitterType, submitterInfo);
          }
        });

        handelInitSettings(formFields, 'form_fields');
      }

      setIsFormPrepared(true);
    }
  }, [formElements]);

  const handlerChange = useCallback(
    (field, value, isTable, index, rowIndex) => {
      const formFields = cloneDeep(formElements);
      const currentIndex = formElements.findIndex((item) => item.id === field.id);

      if (isTable) {
        formFields.forEach((item) => {
          if (item.type === 'table') {
            if (item.rows?.length) {
              if (!get(item, 'rows[0].cols')) {
                item.rows.forEach((row) => {
                  row['cols'] = item.cols.map((col) => {
                    const { autoSetValueFor, type: colType } = col;
                    if (colType?.value === 'date' || colType?.value === 'time') {
                      let value = null;
                      if (autoSetValueFor === userFormRole) {
                        value = new Date();
                      }
                      return {
                        ...col,
                        value,
                      };
                    }
                    return { value: '', id: col.id, type: col.type };
                  });
                });
              }
            }
          }
        });

        if (!formFields[currentIndex].rows[rowIndex]?.cols) {
          formFields[currentIndex].rows.forEach((row, index) => {
            if (!row['cols']) {
              row['cols'] = formFields[currentIndex].cols.map((col) => {
                const { autoSetValueFor, type: colType } = col;

                if (colType?.value === 'date' || colType?.value === 'time') {
                  let value = null;
                  if (autoSetValueFor === userFormRole) {
                    value = new Date();
                  }
                  return {
                    ...col,
                    value,
                  };
                }

                return { value: '', id: col.id, type: col.type };
              });
            }
          });

          if (formFields[currentIndex]?.rows[rowIndex]?.cols[index]) {
            if (formFields[currentIndex].rows[rowIndex].cols[index].type?.value === 'date') {
              formFields[currentIndex].rows[rowIndex].cols[index]['value'] = formFields[currentIndex].rows[rowIndex].cols[index].editable
                ? new Date()
                : null;
            } else if (formFields[currentIndex].rows[rowIndex].cols[index].type?.value === 'time') {
              formFields[currentIndex].rows[rowIndex].cols[index]['value'] = formFields[currentIndex].rows[rowIndex].cols[index].editable
                ? new Date()
                : null;
            } else {
              formFields[currentIndex].rows[rowIndex].cols[index]['value'] = value ?? '';
            }
          }
        } else {
          formFields[currentIndex].rows[rowIndex].cols[index]['value'] = value;
          // } else {
          //   formFields.forEach((item) => {
          //     if (item.type === 'table') {
          //       if (!item.rows[0]['cols']) {
          //         item.rows.forEach((row) => {
          //           row['cols'] = item.cols.map((col) => {
          //             if (col?.type?.value === 'date') {
          //               return {
          //                 value: col.editable ? new Date() : null,
          //                 id: col.id,
          //                 type: col.type,
          //                 editable: col.editable,
          //               };
          //             } else if (col?.type?.value === 'time') {
          //               return {
          //                 value: col.editable ? new Date() : null,
          //                 id: col.id,
          //                 type: col.type,
          //                 editable: col.editable,
          //               };
          //             }
          //             return { value: '', id: col.id, type: col.type };
          //           });
          //         });
          //       }
          //     }
          //   });
        }
      } else if (index >= 0) {
        formFields[currentIndex].inputs = formFields[currentIndex].inputs.map((input, i) => {
          if (i === index) {
            return {
              ...input,
              valueInput: value,
            };
          }
          return input;
        });
      } else {
        if (value?.otherInputValue) {
          formFields[currentIndex].options = {
            ...formFields[currentIndex].options,
            otherInputValue: value.otherInputValue,
            valueInput: value,
          };
        } else {
          formFields[currentIndex].options = {
            ...formFields[currentIndex].options,
            valueInput: value,
            otherInputValue: null,
          };
        }
      }

      if (formFields[currentIndex].options.enableScroll) {
        try {
          const tableRows = formFields[currentIndex].rows.map((item) => {
            delete item.scrollToRow;
            return item;
          });
          const lastNoEmptyRowIndex = tableRows.findLastIndex((row) => {
            let isNoEmptyRow = false;
            if (row.label !== '' && row.label !== null) {
              isNoEmptyRow = true;
            } else if (row.cols) {
              for (const col of row.cols) {
                if (col?.value !== '' && col?.value !== null) {
                  isNoEmptyRow = true;
                  break;
                }
              }
            }
            return isNoEmptyRow;
          });
          const tableRowsLength = tableRows?.length;
          if (lastNoEmptyRowIndex !== -1) {
            tableRows[lastNoEmptyRowIndex].scrollToRow = true;
          } else {
            tableRows[tableRowsLength - 1].scrollToRow = true;
          }
        } catch (e) {
          console.log(e);
        }
      }

      if (field?.type === 'link' || field?.type === 'checkbox') {
        formFields[currentIndex] = field;
      }

      if (field?.type === 'checkbox' && typeof value === 'boolean' && typeof index === 'number' && formFields[currentIndex].inputs[index]) {
        formFields[currentIndex].inputs[index].valueInput = value;
      }

      handelInitSettings(formFields, 'form_fields');
    },
    [formElements, handelInitSettings, userFormRole],
  );

  const handlerChangeFile = (field, value) => {
    const formFields = JSON.parse(JSON.stringify(formElements));

    const currentIndex = formElements.findIndex((item) => item.id === field.id);
    formFields[currentIndex].options['file'] = value;

    handelInitSettings(formFields, 'form_fields');
  };

  const addNewLogin = (field) => {
    const height = field.options.height;
    const currentElementBottom = field.options.top + height;

    let newElement = { ...JSON.parse(JSON.stringify(field)) };
    const index = formElements.findIndex((item) => item.id === field.id);
    newElement.id = new Date().getTime();
    newElement.options.valueInput = '';
    newElement.options.isClone = true;
    newElement.options.top = currentElementBottom;

    const formFields = JSON.parse(JSON.stringify(formElements));
    formFields.forEach((item) => {
      if (item.options.height + item.options.top > currentElementBottom) {
        item.options.top += height;
      }
    });
    formFields.splice(index + 1, 0, newElement);
    handelInitSettings(formFields, 'form_fields');
  };

  const getNewRowData = (rowCols, newRowData = {}) => {
    return {
      label: '',
      newRow: true,
      id: 'new' + new Date().getTime(),
      cols: rowCols.map((col) => {
        const colType = col.type?.value;
        if (colType === 'date' || colType === 'time') {
          const { autoSetValueFor } = col;
          let value = null;

          if (autoSetValueFor === userFormRole) {
            value = new Date();
          }

          return { ...col, value };
        } else if (colType === 'dropdown') {
          const { defaultOptionId, options } = col;
          const newCol = cloneDeep(col);
          if (defaultOptionId && Array.isArray(options)) {
            newCol.value = options.find((o) => o.id === defaultOptionId);
          }

          return newCol;
        }

        return { ...col, value: '' };
      }),
      ...newRowData,
    };
  };

  const addNewRow = (element, newRowIndex) => {
    const formFields = JSON.parse(JSON.stringify(formElements));
    const currentElementIndex = formElements.findIndex((item) => item.id === element.id);
    const currentElement = formFields[currentElementIndex];
    const newRow = getNewRowData(currentElement.cols);

    // If newRowIndex is provided, insert at the specified position, otherwise append to the end of the array
    if (typeof newRowIndex === 'number' && newRowIndex >= 0) {
      const originalIndex = Math.floor(newRowIndex / 2);
      currentElement.rows.splice(originalIndex + 1, 0, newRow);
    } else {
      currentElement.rows.push(newRow);
    }

    // Recalculate heights
    const currentElementBottom = currentElement.options.height + currentElement.options.top;
    formFields.forEach((item) => {
      if (item.options.height + item.options.top > currentElementBottom) {
        item.options.top += 5;
      }
    });
    currentElement.options.height += 5;
    handelInitSettings(formFields, 'form_fields');
  };

  const handlerChangeLabel = (field, value, indexRow) => {
    const formFields = JSON.parse(JSON.stringify(formElements));
    const currentIndex = formElements.findIndex((item) => item.id === field.id);
    formFields[currentIndex].rows[indexRow].label = value;
    handelInitSettings(formFields, 'form_fields');
  };

  // A field makes way for a copied group only when it *starts* at or after the
  // group's insert point. Testing its bottom edge instead also catches a tall
  // field that starts above the group and merely reaches past it — moving that
  // one down tears a gap into the layout above the group and drops the field
  // onto the group itself.
  const startsBelowGroup = (item, groupTop) => (item.options.top ?? 0) >= groupTop;

  const handleCopyFieldsGroup = (groupTop) => {
    const groupFields = cloneDeep(formElements.filter((item) => !!get(item, `options[${ADD_TO_COPY_GROUP_SETTING_KEY}]`)));

    if (groupFields.length) {
      const copyGroupID = new Date().getTime();
      let previousElemOrigBottom;
      let previousElemCurrentBottom;
      let currentElemOrigTop;

      // Creates a new ID and adding a copyGroupID for deletion (group)

      const formattedGroupFields = groupFields.map((item, index) => {
        const newField = { ...item, id: new Date().getTime() + index };
        newField.options.copyGroupID = copyGroupID;

        currentElemOrigTop = newField.options.top;
        if (index === 0) {
          newField.options.top = groupTop;
        } else {
          newField.options.top = previousElemCurrentBottom + Math.min(newField.options.top - previousElemOrigBottom, 0);
        }
        previousElemOrigBottom = currentElemOrigTop + newField.options.height;
        previousElemCurrentBottom = newField.options.top + newField.options.height;

        delete newField.options[ADD_TO_COPY_GROUP_SETTING_KEY];

        // Deleting previous values if they are readable
        if (newField.options.readOnly !== 'readonly') {
          if ('valueInput' in newField.options) {
            newField.options.valueInput = '';
          }
          if ('file' in newField.options) {
            delete newField.options.file;
          }
          if (newField.type === 'table') {
            if (newField.rows?.length) {
              newField.rows = newField.rows.filter((row) => !row.newRow);
              newField.rows.forEach((row, rowIndex) => {
                if (row?.cols) {
                  row.cols.forEach((col, colIndex) => {
                    if (col?.readOnly !== 'readonly') {
                      if (col?.value?.length) {
                        // Set empty col value
                        newField.rows[rowIndex].cols[colIndex].value = '';
                      }
                    }
                  });
                }

                return row;
              });
            }
          }

          if (newField.type === 'checkbox') {
            if (newField.inputs?.length) {
              newField.inputs = newField.inputs.map((input) => {
                input.valueInput = false;
                return input;
              });
            }
          }
        }

        return newField;
      });

      // Exactly the height the copy adds, and exactly what "Delete group" hands back to
      // `handleDeleteCopiedFieldsGroup` -- anything more and a copy/delete round trip
      // leaves the fields below the group lower than it found them. Room for the "Copy a
      // group of fields" button is kept separately, by `copyGroupButtonReserveFor`.
      const groupHeight = previousElemCurrentBottom - groupTop;
      const lastCopyGroupIndex = formElements.findLastIndex((item) => !!item.options.copyGroupID);
      let mergedFields;

      const copyFormElements = formElements.map((item) =>
        startsBelowGroup(item, groupTop) && !item.options.copyGroupID
          ? { ...item, options: { ...item.options, top: item.options.top + groupHeight } }
          : item,
      );

      if (lastCopyGroupElementIndex === -1) {
        mergedFields = [...copyFormElements, ...formattedGroupFields];
      } else {
        const insertIndex = lastCopyGroupIndex === -1 ? lastCopyGroupElementIndex + 1 : lastCopyGroupIndex + 1;

        mergedFields = [...copyFormElements.slice(0, insertIndex), ...formattedGroupFields, ...copyFormElements.slice(insertIndex)];
      }

      handelInitSettings(mergedFields, 'form_fields');
    }
  };

  const handleDeleteCopiedFieldsGroup = (copyGroupID, groupHeight, groupTop) => {
    const filteredFields = cloneDeep(
      formElements.filter((item) => {
        return +get(item, 'options.copyGroupID') !== +copyGroupID;
      }),
    );
    handelInitSettings(
      filteredFields.map((item) =>
        startsBelowGroup(item, groupTop) && !item.options.copyGroupID
          ? { ...item, options: { ...item.options, top: item.options.top - groupHeight } }
          : item,
      ),
      'form_fields',
    );
  };

  // How far a printed field moves down under a grown multiline, and how much further
  // down it reaches for that and for its own growth, in grid units.
  const printMoveFor = (field) => (printGrowth.moves[field.id] || 0) / GridYValue;
  const printExtraFor = (field) => printMoveFor(field) + (printGrowth.growth[field.id] || 0) / GridYValue;

  const renderFromBuilderItem = (field, index, incrementTop, topRelated = 0) => {
    const error = get(errorsObj, `${field.id}`);
    let additionalIncrementTop = 0;
    if (field.type == 'table' && field.options.addNewRowInsideTable) {
      additionalIncrementTop += field.rows.length;
    }

    if (formFieldsAdditionalHeight[field.id]) {
      additionalIncrementTop += formFieldsAdditionalHeight[field.id];
    }

    return [
      <FromBuilderItem
        error={error}
        handlerChangeFile={handlerChangeFile}
        lastIndex={lastIndex}
        index={index}
        addNewLogin={addNewLogin}
        isSubmitter={isSubmitter}
        isPrint={isPrint}
        isPreview={isPreview}
        key={field.id}
        element={field}
        handlerChange={handlerChange}
        addNewRow={addNewRow}
        handlerChangeLabel={handlerChangeLabel}
        duplicateElement={duplicateElement}
        removeElement={removeElement}
        highlightPrevious={highlightPrevious}
        formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
        initiatorID={initiatorID}
        incrementTop={incrementTop + printMoveFor(field)}
        isPrintExpanded={!!printGrowth.expanded[field.id]}
        topRelated={topRelated}
        scale={scale}
        formFieldsAdditionalHeight={formFieldsAdditionalHeight}
        setFormFieldsAdditionalHeight={setFormFieldsAdditionalHeight}
        submitterAddInfoElementID={submitterAddInfoElementID}
        autoFillingVariables={autoFillingVariables}
        submitterType={submitterType}
        submitterInfo={submitterInfo}
        previewTabItem={previewTabItem}
        strictOrderStep={strictOrderStep}
        submitter2Info={submitter2Info}
      />,
      additionalIncrementTop,
    ];
  };

  const renderFieldsData = useMemo(() => {
    const renderedFields = [];
    let currentGroupID = null;

    let areaHeight = 0;
    let groupTop = 0;
    let previousGroupTop;
    let previousGroupHeight;
    let elemsTopIncrement = 0;
    // Where the "Copy a group of fields" button ends up once everything above it has
    // been placed, in `.render-form` coordinates.
    let copyGroupButtonTop = null;
    for (let index = 0; index < formElements?.length; index++) {
      const field = formElements[index];
      const groupID = field.options.copyGroupID;

      if (groupID && groupID !== currentGroupID) {
        // If this is a new groupID, start a new group
        currentGroupID = groupID;

        const groupItems = [];

        // A group member is drawn at `options.top + incrementTop`: the wrapper sits at
        // `groupTop` and the member counts from `-topRelated`, which is that same
        // `groupTop`, so the wrapper's own offset cancels out. Each copy is stored at
        // the exact top `handleCopyFieldsGroup` gave it, so there is nothing left to add
        // -- carrying a running total of the groups above would shift every copy down by
        // the height of all its predecessors, and compound with each new copy.
        let lastElem;
        let groupHoldsButton = false;
        let groupPrintExtra = 0;
        // Add group elements until we encounter an element without groupID
        while (index < formElements.length && formElements[index].options.copyGroupID === currentGroupID) {
          if (!groupTop) {
            groupTop = formElements[index].options.top;
          } else if (previousGroupTop && previousGroupHeight) {
            groupTop = previousGroupTop + previousGroupHeight;
          }
          let [elem, elemIncrementTop] = renderFromBuilderItem(formElements[index], index, elemsTopIncrement, groupTop, true);

          groupItems.push(elem);

          groupHoldsButton = groupHoldsButton || index === lastCopyGroupElementIndex;
          groupPrintExtra = Math.max(groupPrintExtra, printExtraFor(formElements[index]));
          lastElem = formElements[index];
          elemsTopIncrement += elemIncrementTop;
          index++;
        }

        const groupHeight = lastElem ? lastElem.options.top + elemsTopIncrement + lastElem.options.height - groupTop : 0;
        previousGroupTop = groupTop;
        previousGroupHeight = groupHeight;

        areaHeight = Math.max(areaHeight, groupHeight + groupTop + groupPrintExtra);
        // The button hangs off the last field of the group, which is the group's own
        // bottom edge -- the group is stacked at `groupTop`, not at its stored `top`.
        if (groupHoldsButton) {
          copyGroupButtonTop = groupTop + groupHeight;
        }
        renderedFields.push(
          <div
            key={currentGroupID}
            className="render-form__copy-group"
            style={{
              position: 'absolute',
              width: '100%',
              top: groupTop * GridYValue + GridYUnit,
              height: groupHeight * GridYValue + GridYUnit,
            }}
          >
            {groupItems}
            {!isPrint && (
              <div className="render-form__copy-group__actions">
                <button
                  className="btn btn-remove btn-icon render-form__copy-group__remove-btn"
                  onClick={() => handleDeleteCopiedFieldsGroup(groupID, groupHeight, groupTop)}
                >
                  <i className="icon-remove" />
                  <span className={'label'}>Delete group</span>
                </button>
              </div>
            )}
          </div>,
        );

        index--;
        currentGroupID = null;
      } else if (!groupID) {
        let [elem, elemIncrementTop] = renderFromBuilderItem(field, index, elemsTopIncrement + copyGroupButtonReserveFor(field));
        let realHeight = field.options.height;
        if (formFieldsAdditionalHeight[field.id]) {
          realHeight += formFieldsAdditionalHeight[field.id];
        }

        const fieldTop = field.options.top + elemsTopIncrement + copyGroupButtonReserveFor(field);
        areaHeight = Math.max(areaHeight, fieldTop + realHeight + printExtraFor(field));
        if (index === lastCopyGroupElementIndex) {
          copyGroupButtonTop = fieldTop + field.options.height;
        }
        renderedFields.push(elem);
        elemsTopIncrement += elemIncrementTop;
      }
    }

    // Drawn here rather than next to its field: the last copy-group field lives inside
    // the `.render-form__copy-group` wrapper once a copy exists, and the button's
    // `top` is a form coordinate, so rendering it there offset it by the wrapper's own
    // `top` -- leaving a gap under the group and stretching the form into a scrollbar.
    if (showsCopyGroupButton && copyGroupButtonTop !== null) {
      areaHeight = Math.max(areaHeight, copyGroupButtonTop + COPY_GROUP_BUTTON_RESERVE);
      renderedFields.push(
        <div
          key="copy-group-button"
          style={{
            width: '100%',
            display: 'flex',
            paddingRight: 24,
            justifyContent: 'flex-end',
            position: 'absolute',
            top: copyGroupButtonTop * GridYValue + GridYUnit,
            height: '40px',
            zIndex: 999999999,
          }}
        >
          <button className={'btn'} onClick={() => handleCopyFieldsGroup(copyGroupInsertTop)}>
            Copy a group of fields
          </button>
        </div>,
      );
    }

    return { fields: renderedFields, height: Math.max(areaHeight, 15) };
  }, [JSON.stringify(formElements), handleDeleteCopiedFieldsGroup, renderFromBuilderItem, formFieldsAdditionalHeight, printGrowth]);

  useEffect(() => {
    const updateScale = () => {
      if (renderFormRef.current?.offsetWidth) {
        setScale(Math.min(renderFormRef.current.offsetWidth / 1100, 1));
      }
    };

    const resizeObserver = new ResizeObserver(updateScale);
    if (renderFormRef.current) {
      resizeObserver.observe(renderFormRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // How far a printed text runs past where its field was meant to end: its last character,
  // with the room the field leaves under its text (the text's own padding, the field's
  // padding and borders), against its designed top and height - the top before a legacy
  // text's shift down (form-item.js), so a text that fills a shifted box runs into the field
  // under it by that shift, and moves it. Layout px, whatever the form's scale.
  const textOverrun = (fieldEl, text) => {
    const bottom = textContentBottom(text);
    if (bottom === null) return 0;

    const rect = fieldEl.getBoundingClientRect();
    const zoom = rect.height ? fieldEl.offsetHeight / rect.height : 1;
    const px = (el, property) => parseFloat(window.getComputedStyle(el)[property]) || 0;
    const room =
      px(text, 'paddingBottom') +
      px(text.parentElement, 'borderBottomWidth') +
      px(fieldEl, 'paddingBottom') +
      px(fieldEl, 'borderBottomWidth');
    const designedEnd = parseFloat(fieldEl.dataset.designHeight) - (parseFloat(fieldEl.dataset.topShift) || 0);

    return Math.max(0, Math.round((bottom - rect.top) * zoom + room - designedEnd));
  };

  // Reads where every printed field is drawn and how much each multiline answer grew,
  // picks out the text fields that hide text in their box - those print in full, and once
  // they do they hide nothing, so they stay picked - and works out the moves from that. Positions are layout px of `.render-form`
  // (offsets, so the scale transform does not enter), taken on what a field shows - its
  // inner wrapper, not the padding round it - with the move it is drawn with now taken
  // back out, so the rule always sees the form as designed.
  const measurePrintGrowth = () => {
    const area = fieldsAreaRef.current;
    if (!area) return;

    const offsetIn = (el) => {
      let top = 0;
      let left = 0;
      for (let node = el; node && node !== area; node = node.offsetParent) {
        top += node.offsetTop;
        left += node.offsetLeft;
      }
      return { top, left };
    };

    const expanded = { ...printGrowthRef.current.expanded };
    const fields = [...area.querySelectorAll('[id^="element-"]')].map((fieldEl) => {
      const id = fieldEl.id.slice('element-'.length);
      const content = fieldEl.querySelector('.form-builder-element-preview-wrapper') || fieldEl;
      const { top, left } = offsetIn(content);
      const move = printGrowthRef.current.moves[id] || 0;
      // The printed multiline answer (multiline-element.js) keeps the form's height as its
      // minimum, so anything past that is the growth.
      const answer = fieldEl.querySelector('pre.textarea');
      let growth = answer ? Math.max(0, answer.offsetHeight - parseFloat(answer.style.minHeight)) : 0;
      const text = fieldEl.querySelector('.html-area');

      if (text && !expanded[id]) {
        // What scrolls: the text's own box where it is pinned to the constructor's height
        // (text-element.jsx), the whole field otherwise.
        expanded[id] = hidesContent(text.parentElement.style.height ? text : fieldEl) || undefined;
      }

      if (text) {
        growth = textOverrun(fieldEl, text);
      }

      return { id, top: top - move, bottom: top - move + content.offsetHeight, left, right: left + content.offsetWidth, growth };
    });

    const next = {
      moves: multilineGrowthPushes(fields),
      growth: Object.fromEntries(fields.filter((field) => field.growth > 0).map((field) => [field.id, field.growth])),
      expanded: Object.fromEntries(Object.entries(expanded).filter(([, isExpanded]) => isExpanded)),
    };
    setPrintGrowth((current) => (isEqual(current, next) ? current : next));
  };

  // After every render: a field can be drawn somewhere else for reasons of its own (a
  // table or checkbox that grew moves what follows it).
  useLayoutEffect(() => {
    if (isPrint && isFormPrepared) {
      measurePrintGrowth();
    }
  });

  // And whenever a field changes size without a render, e.g. once the font has loaded.
  useEffect(() => {
    const area = fieldsAreaRef.current;
    if (!isPrint || !isFormPrepared || !area) return undefined;

    const resizeObserver = new ResizeObserver(() => measurePrintGrowth());
    area.querySelectorAll('.form-builder-element-preview-wrapper').forEach((el) => resizeObserver.observe(el));

    // And when a legacy text settles how far it is drawn down: it learns its width only once
    // laid out (form-item.js), which moves it without resizing anything, and a move measured
    // before that fell short by the rest of the shift (form 1179: 35px of 77).
    const mutationObserver = new MutationObserver(() => measurePrintGrowth());
    mutationObserver.observe(area, { subtree: true, attributes: true, attributeFilter: ['data-top-shift'] });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isPrint, isFormPrepared, formElements]);

  const renderMobileItem = (field, index) => {
    const error = get(errorsObj, `${field.id}`);

    return (
      <FromBuilderItem
        isMobileView
        error={error}
        handlerChangeFile={handlerChangeFile}
        lastIndex={lastIndex}
        index={index}
        addNewLogin={addNewLogin}
        isSubmitter={isSubmitter}
        isPrint={isPrint}
        isPreview={isPreview}
        key={field.id}
        element={field}
        handlerChange={handlerChange}
        addNewRow={addNewRow}
        handlerChangeLabel={handlerChangeLabel}
        duplicateElement={duplicateElement}
        removeElement={removeElement}
        highlightPrevious={highlightPrevious}
        formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
        initiatorID={initiatorID}
        formFieldsAdditionalHeight={formFieldsAdditionalHeight}
        setFormFieldsAdditionalHeight={setFormFieldsAdditionalHeight}
        submitterAddInfoElementID={submitterAddInfoElementID}
        autoFillingVariables={autoFillingVariables}
        submitterType={submitterType}
        submitterInfo={submitterInfo}
        previewTabItem={previewTabItem}
        strictOrderStep={strictOrderStep}
        submitter2Info={submitter2Info}
      />
    );
  };

  // Vertical layout for phones: order fields by their real position on the
  // canvas (top first, then left) and render them one under another. Copy
  // groups are kept together as a single block.
  const renderMobileFields = () => {
    if (!Array.isArray(formElements)) return null;

    const groupsMap = new Map();
    const units = [];

    formElements.forEach((field, index) => {
      const top = field.options.top ?? 0;
      const left = field.options.left ?? 0;
      const groupID = field.options.copyGroupID;

      if (groupID) {
        let unit = groupsMap.get(groupID);
        if (!unit) {
          unit = { type: 'group', groupID, top, left, items: [] };
          groupsMap.set(groupID, unit);
          units.push(unit);
        }
        unit.items.push({ field, index });
        unit.top = Math.min(unit.top, top);
        unit.left = Math.min(unit.left, left);
      } else {
        units.push({ type: 'single', field, index, top, left });
      }
    });

    units.sort((a, b) => a.top - b.top || a.left - b.left);

    const renderedFields = units.map((unit) => {
      if (unit.type === 'single') {
        return renderMobileItem(unit.field, unit.index);
      }

      const sortedItems = [...unit.items].sort(
        (a, b) => (a.field.options.top ?? 0) - (b.field.options.top ?? 0) || (a.field.options.left ?? 0) - (b.field.options.left ?? 0),
      );
      const groupTop = unit.top;
      const groupHeight = Math.max(...unit.items.map(({ field }) => (field.options.top ?? 0) + (field.options.height ?? 0))) - groupTop;

      return (
        <div key={unit.groupID} className="render-form__copy-group">
          {sortedItems.map(({ field, index }) => renderMobileItem(field, index))}
          {!isPrint && (
            <div className="render-form__copy-group__actions">
              <button
                className="btn btn-remove btn-icon render-form__copy-group__remove-btn"
                onClick={() => handleDeleteCopiedFieldsGroup(unit.groupID, groupHeight, groupTop)}
              >
                <i className="icon-remove" />
                <span className={'label'}>Delete group</span>
              </button>
            </div>
          )}
        </div>
      );
    });

    if (!isPrint && isSubmitter && isExistElementsCopyGroup) {
      renderedFields.push(
        <div
          key="copy-group-action"
          style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', paddingRight: 24, marginBottom: 24 }}
        >
          <button className={'btn'} onClick={() => handleCopyFieldsGroup(copyGroupTemplateBottom)}>
            Copy a group of fields
          </button>
        </div>,
      );
    }

    return renderedFields;
  };

  if (isMobileView) {
    return (
      <div className="render-form-wrapper render-form-wrapper-mobile" style={{ background: '#fff' }}>
        <TitleElement formData={formData} element={title}></TitleElement>
        <div className={`${isDisabled ? 'render-form-disabled' : ''} render-form render-form-mobile`}>
          {isDisabled && <span className={'is-disabled'}>Deactivated: {dayjs(new Date(isDisabled)).format(DATE_FORMAT)}</span>}
          {isFormPrepared && renderMobileFields()}
        </div>
        {!!showDummyActions && isFormPrepared && (
          <div className="form-control-actions">
            <button className={'btn btn-outline mr-2'}>Clear form</button>
            <button className="btn">Submit</button>
          </div>
        )}
      </div>
    );
  }

  // `.render-form` is moved up by `topShiftPx` together with its absolutely
  // positioned fields, so it still needs to be tall enough for all of them --
  // anything shorter and it scrolls (`overflow-x: hidden` turns the y axis into
  // `auto`). The shift only moves the fields up on screen, so it is the
  // scale wrapper -- the box that actually takes up space in the page -- that
  // reserves that much less room, which is what keeps the dead white space off
  // the bottom of the form.
  const topShiftPx = (isMobile ? FIELDS_TOP_SHIFT_MOBILE : FIELDS_TOP_SHIFT) * scale;
  const formContentHeight = (renderFieldsData.height - FIELD_TOP_GRID_OFFSET) * GridYValue + FORM_BOTTOM_PADDING;
  const formAreaHeight = Math.max(formContentHeight - topShiftPx, 0);

  return (
    <div
      className="render-form-wrapper"
      style={
        isMobile
          ? { background: '#fff' }
          : isPreview
            ? { background: '#fff' }
            : {
                border: '1px solid var(--divider, #eaecf0)',
                boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.06), 0 1px 3px 0 rgba(16, 24, 40, 0.1)',
                background: '#fff',
                borderRadius: '8px',
              }
      }
    >
      <TitleElement formData={formData} element={title}></TitleElement>
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          height: scale ? formAreaHeight * scale + GridYUnit : 'unset',
        }}
        className="render-form-scale-wrapper"
        ref={renderFormRef}
      >
        <div
          className={`${isDisabled ? 'render-form-disabled' : ''} render-form`}
          ref={fieldsAreaRef}
          style={{
            position: 'relative',
            top: `-${topShiftPx}px`,
            height: scale ? formContentHeight + GridYUnit : 'unset',
            visibility: scale ? 'visible' : 'hidden',
            width: '1100px',
            background: 'unset',
            border: 'unset',
            boxShadow: 'unset',
            borderRadius: 'unset',
          }}
        >
          {isDisabled && <span className={'is-disabled'}>Deactivated: {dayjs(new Date(isDisabled)).format(DATE_FORMAT)}</span>}
          {isFormPrepared && renderFieldsData.fields}
        </div>
      </div>
      {!!showDummyActions && isFormPrepared && (
        <div className="form-control-actions">
          <button className={'btn btn-outline mr-2'}>Clear form</button>
          <button className="btn">Submit</button>
        </div>
      )}
    </div>
  );
};
